import { InputField } from "../../components/common/input-field";
import { toast } from "sonner";
import { getPasswordStrength } from "@/utils/passwordStrength";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useForm, useWatch } from "react-hook-form";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { PasswordFormValues } from "./schema/password.schema";
import { passwordSchema } from "./schema/password.schema";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuthStore } from "@/stores/auth.store";
import { ChangePassword } from "@/services/account.service";
import { useNavigate } from "react-router-dom";

const FIELD_CLASS =
  "h-14 rounded-2xl border-border bg-card px-4 pr-14 text-sm font-medium text-muted-foreground placeholder:text-border";

export function PasswordForm() {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    mode: "onChange",
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const navigate = useNavigate();

  const newPassword = useWatch({ control, name: "newPassword" });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onSubmit = async (data: PasswordFormValues) => {
    const agentCompanySellerId =
      useAuthStore.getState().user?.agentCompanySellerId;

    if (!agentCompanySellerId) {
      toast.error("Agent company seller ID is missing.");
      return;
    }

    try {
      await ChangePassword({
        agentCompanySellerId,
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        confirmNewPassword: data.confirmNewPassword,
      });

      await new Promise((r) => setTimeout(r, 1500));
      toast.success("Password updated successfully.");
      navigate(-1);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update password. Please try again later.",
      );
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="rounded-2xl px-4 py-6 shadow-2xl bg-card"
    >
      <div className="flex flex-col gap-4 md:grid md:grid-cols-3">
        <div className="relative">
          <InputField
            label="Current Password"
            placeholder="Enter current password"
            required
            type={showCurrentPassword ? "text" : "password"}
            className={cn(
              FIELD_CLASS,
              errors.currentPassword && "border-destructive",
            )}
            {...register("currentPassword")}
            error={errors.currentPassword?.message}
          />

          <button
            type="button"
            onClick={() => setShowCurrentPassword((prev) => !prev)}
            className="absolute right-4 top-[2.65rem] cursor-pointer text-muted hover:text-foreground"
            aria-label={showCurrentPassword ? "Hide password" : "Show password"}
          >
            {showCurrentPassword ? (
              <Eye className="size-4" />
            ) : (
              <EyeOff className="size-4" />
            )}
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <div className="relative">
            <InputField
              label="New Password"
              placeholder="Enter new password"
              required
              type={showNewPassword ? "text" : "password"}
              className={cn(
                FIELD_CLASS,
                errors.newPassword && "border-destructive",
              )}
              {...register("newPassword")}
              error={errors.newPassword?.message}
            />

            <button
              type="button"
              onClick={() => setShowNewPassword((prev) => !prev)}
              className="absolute right-4 top-[2.65rem] cursor-pointer text-muted hover:text-foreground"
              aria-label={showNewPassword ? "Hide password" : "Show password"}
            >
              {showNewPassword ? (
                <Eye className="size-4" />
              ) : (
                <EyeOff className="size-4" />
              )}
            </button>
          </div>

          <PasswordStrengthBar password={newPassword} />
        </div>

        <div className="relative">
          <InputField
            label="Confirm New Password"
            placeholder="Enter confirm new password"
            required
            type={showConfirmPassword ? "text" : "password"}
            className={cn(
              FIELD_CLASS,
              errors.confirmNewPassword && "border-destructive",
            )}
            {...register("confirmNewPassword")}
            error={errors.confirmNewPassword?.message}
          />

          <button
            type="button"
            onClick={() => setShowConfirmPassword((prev) => !prev)}
            className="absolute right-4 top-[2.65rem] cursor-pointer text-muted hover:text-foreground"
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            {showConfirmPassword ? (
              <Eye className="size-4" />
            ) : (
              <EyeOff className="size-4" />
            )}
          </button>
        </div>

        <p className="text-sm font-medium text-muted leading-tight md:col-span-2 md:flex md:items-center">
          Set password (8-20 characters with at least 1 number, 1 letter and 1
          special symbol)
        </p>

        <Button
          variant="default"
          className="w-full cursor-pointer"
          disabled={!isValid || isSubmitting}
          type="submit"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="animate-spin" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </form>
  );
}

function PasswordStrengthBar({ password }: { password: string }) {
  if (!password) return null;

  const { score, label, color } = getPasswordStrength(password);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "h-1.5 flex-1 rounded-full transition-colors",
              i < score ? color : "bg-border",
            )}
          />
        ))}
      </div>
      <span className="text-xs font-medium text-field">{label}</span>
    </div>
  );
}
